/**
 * CrisisFeedbackBottomSheet Component
 *
 * Streamlined 2-step feedback UI for crisis detection accuracy reporting.
 * Replaces the 4-stage modal wizard with a minimal bottom sheet.
 *
 * Design Principles (Multi-Model Consensus 2025-12-21):
 * - USER-INITIATED: Only shown when user taps "Was this banner accurate?"
 * - 2-TAP FLOW: Category selection → Submit (vs previous 4+ taps)
 * - VISUAL CHIPS: 2x3 grid for quick selection, radiogroup semantics
 * - ACCESSIBILITY: WCAG AAA (48px targets, 7:1 contrast, keyboard nav)
 * - PRIVACY: Categorical feedback only, no message content stored
 *
 * Categories:
 * - "Accurate – Resources Helped" (was_accurate: true)
 * - False positive sub-tags: Roleplay, Quote, Sarcasm, Lyrics, Academic, Historical, Other
 *
 * @see crisis-feedback-submit Edge Function for backend
 * @see CrisisBanner for integration point
 */

import {
  View,
  Text,
  Pressable,
  Modal,
  StyleSheet,
  Platform,
  ScrollView,
} from 'react-native';
// Use keyboard-controller's KeyboardAvoidingView for reliable behavior across all devices
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from '@dramadetector/shared';
import { captureEvent } from '../../lib/analytics';
import { SAFETY_FEEDBACK_V2_EVENTS } from '../../lib/analytics/events/safety';
import { useAuth } from '../../contexts/AuthContext';
import { authenticatedFetch } from '@dramadetector/api-client';
import { getEdgeFunctionUrl, assertResponseOk } from '../../lib/api-url';

export interface CrisisFeedbackBottomSheetProps {
  /** Whether the bottom sheet is visible */
  visible: boolean;
  /** UUID of the moderation event to provide feedback for */
  moderationEventId?: string;
  /** Severity level from crisis detection */
  severity?: 'low' | 'medium' | 'high' | 'critical';
  /** Callback when sheet is closed */
  onClose: () => void;
  /** Callback when feedback is successfully submitted */
  onSubmitted?: () => void;
}

/** Category option for the feedback chips */
interface CategoryOption {
  key: string;
  wasAccurate: boolean;
  categoryFeedback?: string;
  icon: string;
}

/** Sub-tag options for false positive feedback */
const FALSE_POSITIVE_SUBTAGS: CategoryOption[] = [
  { key: 'roleplay', wasAccurate: false, categoryFeedback: 'fictional', icon: '🎭' },
  { key: 'quote', wasAccurate: false, categoryFeedback: 'creative', icon: '💬' },
  { key: 'sarcasm', wasAccurate: false, categoryFeedback: 'creative', icon: '😏' },
  { key: 'lyrics', wasAccurate: false, categoryFeedback: 'creative', icon: '🎵' },
  { key: 'academic', wasAccurate: false, categoryFeedback: 'academic', icon: '📚' },
  { key: 'historical', wasAccurate: false, categoryFeedback: 'historical', icon: '📜' },
  { key: 'other', wasAccurate: false, categoryFeedback: 'other', icon: '❓' },
];

/** Analytics event names - use centralized constants */
const FEEDBACK_V2_ANALYTICS = SAFETY_FEEDBACK_V2_EVENTS;

/**
 * CrisisFeedbackBottomSheet - Streamlined crisis detection feedback UI
 */
export function CrisisFeedbackBottomSheet({
  visible,
  moderationEventId,
  severity = 'low',
  onClose,
  onSubmitted,
}: CrisisFeedbackBottomSheetProps) {
  const { t } = useTranslation('safety');
  const { user, accessToken } = useAuth();

  // UI State
  const [step, setStep] = useState<'category' | 'subtag'>('category');
  const [selectedCategory, setSelectedCategory] = useState<'accurate' | 'false_positive' | null>(null);
  const [selectedSubtag, setSelectedSubtag] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs for keyboard navigation
  const chipRefs = useRef<(View | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);

  // Reset state when sheet opens
  useEffect(() => {
    if (visible) {
      setStep('category');
      setSelectedCategory(null);
      setSelectedSubtag(null);
      setError(null);
      setFocusedIndex(0);

      captureEvent(FEEDBACK_V2_ANALYTICS.SHEET_OPENED, {
        moderation_event_id: moderationEventId,
        severity,
      });
    }
  }, [visible, moderationEventId, severity]);

  // Handle close
  const handleClose = useCallback(() => {
    captureEvent(FEEDBACK_V2_ANALYTICS.SHEET_CLOSED, {
      step,
      had_selection: selectedCategory !== null,
      moderation_event_id: moderationEventId,
    });
    onClose();
  }, [step, selectedCategory, moderationEventId, onClose]);

  // Handle back button
  const handleBack = useCallback(() => {
    setStep('category');
    setSelectedSubtag(null);
    setFocusedIndex(0);
  }, []);

  // Submit feedback to Edge Function
  const submitFeedback = useCallback(async (wasAccurate: boolean, categoryFeedback?: string) => {
    if (!moderationEventId || !user) {
      setError(t('detection_feedback.error_message'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (!accessToken) throw new Error('Not authenticated');

      // NocoDB #4128 (2026-05-08): migrated from raw fetch() to authenticatedFetch().
      // Bearer token + Accept-Version + cold-load pre-gate handled by the wrapper.
      const response = await authenticatedFetch(getEdgeFunctionUrl('crisis-feedback-submit'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          moderation_event_id: moderationEventId,
          was_accurate: wasAccurate,
          category_feedback: categoryFeedback,
        }),
      });

      await assertResponseOk(response, 'Failed to submit feedback');

      const data = await response.json();

      if (data?.error) {
        throw new Error(data.error);
      }

      captureEvent(FEEDBACK_V2_ANALYTICS.SUBMITTED, {
        was_accurate: wasAccurate,
        category_feedback: categoryFeedback,
        moderation_event_id: moderationEventId,
        feedback_id: data?.id,
      });

      onSubmitted?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(t('detection_feedback.error_message'));

      captureEvent(FEEDBACK_V2_ANALYTICS.ERROR, {
        error: message,
        moderation_event_id: moderationEventId,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [moderationEventId, user, accessToken, t, onSubmitted]);

  // P1 Accessibility: Keyboard navigation handler
  const handleKeyDown = useCallback(
    (event: { key: string; preventDefault: () => void }, index: number, totalItems: number) => {
      let newIndex = index;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          newIndex = (index + 1) % totalItems;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          newIndex = (index - 1 + totalItems) % totalItems;
          break;
        case 'Home':
          event.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          newIndex = totalItems - 1;
          break;
        case 'Escape':
          event.preventDefault();
          handleClose();
          return;
        case 'Enter':
        case ' ':
          event.preventDefault();
          // Selection handled by onPress
          return;
        default:
          return;
      }

      setFocusedIndex(newIndex);
      chipRefs.current[newIndex]?.focus?.();
    },
    [handleClose]
  );

  // Handle category selection
  const handleCategorySelect = useCallback(
    async (category: 'accurate' | 'false_positive') => {
      setSelectedCategory(category);

      captureEvent(FEEDBACK_V2_ANALYTICS.CATEGORY_SELECTED, {
        category,
        moderation_event_id: moderationEventId,
      });

      if (category === 'accurate') {
        // Submit immediately for accurate feedback
        await submitFeedback(true, 'actual_crisis');
      } else {
        // Show subtag selection for false positives
        setStep('subtag');
        setFocusedIndex(0);
      }
    },
    [moderationEventId, submitFeedback]
  );

  // Handle subtag selection
  const handleSubtagSelect = useCallback(
    async (subtag: CategoryOption) => {
      setSelectedSubtag(subtag.key);

      captureEvent(FEEDBACK_V2_ANALYTICS.SUBTAG_SELECTED, {
        subtag: subtag.key,
        category_feedback: subtag.categoryFeedback,
        moderation_event_id: moderationEventId,
      });

      await submitFeedback(false, subtag.categoryFeedback);
    },
    [moderationEventId, submitFeedback]
  );

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={handleClose}
      accessibilityViewIsModal
    >
      <Pressable style={styles.overlay} onPress={handleClose}>
        <KeyboardAvoidingView
          behavior="padding"
          style={styles.keyboardAvoid}
        >
          <Pressable
            style={styles.sheet}
            onPress={(e) => e.stopPropagation()}
            accessibilityLabel={t('detection_feedback.sheet_title')}
          >
            {/* Handle bar */}
            <View style={styles.handleBar} />

            {/* Header */}
            <View style={styles.header}>
              {step === 'subtag' && (
                <Pressable
                  onPress={handleBack}
                  style={styles.backButton}
                  accessibilityRole="button"
                  accessibilityLabel="Go back"
                >
                  <Text style={styles.backButtonText}>←</Text>
                </Pressable>
              )}
              <View style={styles.headerTextContainer}>
                <Text style={styles.title}>{t('detection_feedback.sheet_title')}</Text>
                <Text style={styles.subtitle}>{t('detection_feedback.sheet_subtitle')}</Text>
              </View>
              <Pressable
                onPress={handleClose}
                style={styles.closeButton}
                accessibilityRole="button"
                accessibilityLabel={t('modal.close')}
              >
                <Text style={styles.closeButtonText}>✕</Text>
              </Pressable>
            </View>

            {/* Error message */}
            {error && (
              <View style={styles.errorContainer}>
                <Text style={styles.errorText}>{error}</Text>
              </View>
            )}

            {/* Content */}
            <ScrollView
              style={styles.content}
              contentContainerStyle={styles.contentContainer}
              keyboardShouldPersistTaps="handled"
            >
              {step === 'category' ? (
                /* Step 1: Category selection */
                <View
                  style={styles.chipGrid}
                  accessibilityRole="radiogroup"
                  accessibilityLabel="Select feedback category"
                >
                  {/* Accurate chip */}
                  <Pressable
                    ref={(ref) => { chipRefs.current[0] = ref; }}
                    onPress={() => handleCategorySelect('accurate')}
                    // @ts-expect-error onKeyDown not in Pressable types but valid on web
                    onKeyDown={
                      Platform.OS === 'web'
                        ? (e: any) => handleKeyDown(e, 0, 2)
                        : undefined
                    }
                    style={[
                      styles.chip,
                      styles.chipAccurate,
                      selectedCategory === 'accurate' && styles.chipSelected,
                    ]}
                    accessibilityRole="radio"
                    accessibilityState={{
                      checked: selectedCategory === 'accurate',
                    }}
                    accessibilityLabel={t('detection_feedback.category_accurate_resources_helped')}
                    disabled={isSubmitting}
                    testID="feedback-chip-accurate"
                  >
                    <Text style={styles.chipIcon}>✓</Text>
                    <Text style={[styles.chipText, styles.chipTextAccurate]}>
                      {t('detection_feedback.category_accurate_resources_helped')}
                    </Text>
                  </Pressable>

                  {/* False positive chip */}
                  <Pressable
                    ref={(ref) => { chipRefs.current[1] = ref; }}
                    onPress={() => handleCategorySelect('false_positive')}
                    // @ts-expect-error onKeyDown not in Pressable types but valid on web
                    onKeyDown={
                      Platform.OS === 'web'
                        ? (e: any) => handleKeyDown(e, 1, 2)
                        : undefined
                    }
                    style={[
                      styles.chip,
                      styles.chipFalsePositive,
                      selectedCategory === 'false_positive' && styles.chipSelected,
                    ]}
                    accessibilityRole="radio"
                    accessibilityState={{
                      checked: selectedCategory === 'false_positive',
                    }}
                    accessibilityLabel={t('detection_feedback.inaccurate_button')}
                    disabled={isSubmitting}
                    testID="feedback-chip-false-positive"
                  >
                    <Text style={styles.chipIcon}>✗</Text>
                    <Text style={[styles.chipText, styles.chipTextFalsePositive]}>
                      {t('detection_feedback.inaccurate_button')}
                    </Text>
                  </Pressable>
                </View>
              ) : (
                /* Step 2: Subtag selection */
                <View
                  style={styles.subtagGrid}
                  accessibilityRole="radiogroup"
                  accessibilityLabel="Select reason"
                >
                  {FALSE_POSITIVE_SUBTAGS.map((subtag, index) => (
                    <Pressable
                      key={subtag.key}
                      ref={(ref) => { chipRefs.current[index] = ref; }}
                      onPress={() => handleSubtagSelect(subtag)}
                      // @ts-expect-error onKeyDown not in Pressable types but valid on web
                      onKeyDown={
                        Platform.OS === 'web'
                          ? (e: any) =>
                              handleKeyDown(e, index, FALSE_POSITIVE_SUBTAGS.length)
                          : undefined
                      }
                      style={[
                        styles.subtagChip,
                        selectedSubtag === subtag.key && styles.chipSelected,
                      ]}
                      accessibilityRole="radio"
                      accessibilityState={{
                        checked: selectedSubtag === subtag.key,
                      }}
                      accessibilityLabel={t(`detection_feedback.subtag_${subtag.key}` as any)}
                      disabled={isSubmitting}
                      testID={`feedback-subtag-${subtag.key}`}
                    >
                      <Text style={styles.subtagIcon}>{subtag.icon}</Text>
                      <Text style={styles.subtagText}>
                        {t(`detection_feedback.subtag_${subtag.key}` as any)}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              )}

              {/* Loading indicator */}
              {isSubmitting && (
                <View style={styles.loadingContainer}>
                  <Text style={styles.loadingText}>
                    {t('detection_feedback.submitting')}
                  </Text>
                </View>
              )}
            </ScrollView>

            {/* Privacy notice */}
            <View style={styles.footer}>
              <Text style={styles.privacyText}>
                {t('detection_feedback.privacy_notice')}
              </Text>
            </View>
          </Pressable>
        </KeyboardAvoidingView>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  keyboardAvoid: {
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '70%',
    minHeight: 300,
  },
  handleBar: {
    width: 40,
    height: 4,
    backgroundColor: '#D1D5DB',
    borderRadius: 2,
    alignSelf: 'center',
    marginTop: 12,
    marginBottom: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  headerTextContainer: {
    flex: 1,
  },
  backButton: {
    padding: 8,
    marginRight: 8,
    minWidth: 44,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backButtonText: {
    fontSize: 20,
    color: '#374151',
  },
  closeButton: {
    padding: 8,
    minWidth: 44,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButtonText: {
    fontSize: 18,
    color: '#6B7280',
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  subtitle: {
    fontSize: 14,
    color: '#6B7280',
    marginTop: 2,
  },
  errorContainer: {
    backgroundColor: '#FEF2F2',
    padding: 12,
    marginHorizontal: 16,
    marginTop: 8,
    borderRadius: 8,
  },
  errorText: {
    color: '#991B1B',
    fontSize: 14,
    textAlign: 'center',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
  },
  chipGrid: {
    flexDirection: 'column',
    gap: 12,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: 12,
    borderWidth: 2,
    minHeight: 56, // Accessibility: exceeds 48px minimum
    gap: 8,
  },
  chipAccurate: {
    backgroundColor: '#ECFDF5', // green-50
    borderColor: '#A7F3D0', // green-200
  },
  chipFalsePositive: {
    backgroundColor: '#FEF3C7', // amber-50
    borderColor: '#FDE68A', // amber-200
  },
  chipSelected: {
    borderWidth: 3,
    borderColor: '#7C3AED', // violet-600
  },
  chipIcon: {
    fontSize: 18,
  },
  chipText: {
    fontSize: 16,
    fontWeight: '500',
  },
  chipTextAccurate: {
    color: '#065F46', // green-800
  },
  chipTextFalsePositive: {
    color: '#92400E', // amber-800
  },
  subtagGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'space-between',
  },
  subtagChip: {
    width: '48%', // 2-column layout
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E5E7EB', // gray-200
    backgroundColor: '#F9FAFB', // gray-50
    minHeight: 52, // Accessibility
    gap: 8,
  },
  subtagIcon: {
    fontSize: 16,
  },
  subtagText: {
    fontSize: 13,
    color: '#374151', // gray-700
    flex: 1,
    flexWrap: 'wrap',
  },
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: 16,
  },
  loadingText: {
    color: '#6B7280',
    fontSize: 14,
  },
  footer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
  },
  privacyText: {
    fontSize: 12,
    color: '#9CA3AF',
    textAlign: 'center',
  },
});

export default CrisisFeedbackBottomSheet;
